import React, { useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { FiCheck, FiMapPin, FiPhone, FiUser } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import banner from "./assets/img/logo_transparent.png";
import "./BoxComplete.css";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "https://systalk-dev.azure-api.net/wagtag";

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY ||
    (process.env.NODE_ENV === "development" ? "1x00000000000000000000AA" : "");
const contactDetailsStorageKey = "wagtagBoxCompleteContactDetails";

const getSavedContactDetails = () => {
    try {
        return JSON.parse(localStorage.getItem(contactDetailsStorageKey)) || {};
    } catch {
        return {};
    }
};

const saveContactDetails = (contactDetails) => {
    try {
        localStorage.setItem(contactDetailsStorageKey, JSON.stringify(contactDetails));
    } catch (error) {
        console.warn("Unable to save box contact details in this browser:", error);
    }
};

function BoxComplete() {
    const [searchParams] = useSearchParams();
    const turnstileRef = useRef(null);
    const boxCode = (searchParams.get("bc") || "").trim();
    const [savedContactDetails] = useState(getSavedContactDetails);
    const [name, setName] = useState(savedContactDetails.name || "");
    const [phone, setPhone] = useState(savedContactDetails.phone || "");
    const [location, setLocation] = useState(savedContactDetails.location || "");
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileErrorMessage, setTurnstileErrorMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const isComplete = name.trim() && phone.trim() && location.trim() && boxCode && turnstileToken;

    const resetTurnstile = () => {
        setTurnstileToken("");
        turnstileRef.current?.reset();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (submitting) return;

        if (!boxCode) {
            setErrorMessage("This box QR code is missing or invalid. Please scan the QR code on the box again.");
            return;
        }

        if (!name.trim() || !phone.trim() || !location.trim()) {
            setErrorMessage("Please complete all required fields.");
            return;
        }

        if (!turnstileToken) {
            setTurnstileErrorMessage("Please complete the security check before submitting.");
            return;
        }

        setSubmitting(true);
        setErrorMessage("");
        setTurnstileErrorMessage("");

        try {
            const response = await fetch(`${apiBaseUrl}/api/Tag/ReportBoxEmpty`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Name: name.trim(),
                    PhoneNumber: phone.trim(),
                    BoxCode: boxCode,
                    Location: location.trim(),
                    TurnstileToken: turnstileToken,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const apiErrorMessage = typeof errorData === "string"
                    ? errorData
                    : errorData?.Message || errorData?.message || errorData?.title || "We couldn't record this box. Please try again.";
                throw new Error(apiErrorMessage);
            }

            saveContactDetails({
                name: name.trim(),
                phone: phone.trim(),
                location: location.trim(),
            });
            setSubmitted(true);
        } catch (error) {
            console.error("Error reporting empty WagTag box:", error);
            setErrorMessage(error.message || "We couldn't record this box. Please try again.");
            resetTurnstile();
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="box-complete-page">
                <header className="box-header">
                    <div className="box-header-inner">
                        <img src={banner} alt="WagTag" className="box-logo" />
                        <div className="box-header-label">
                            <strong>WagTag Stock</strong>
                            <span>Stock notification</span>
                        </div>
                    </div>
                </header>
                <main className="box-main">
                    <section className="box-confirmation" aria-live="polite">
                        <span className="box-success-icon"><FiCheck aria-hidden="true" /></span>
                        <p className="box-eyebrow">NOTIFICATION RECEIVED</p>
                        <h1>Thanks, {name.trim()}.</h1>
                        <p>We’ve recorded that this box of WagTags has been finished at <strong>{location.trim()}</strong>.</p>
                        <p>This helps us keep an eye on stock and replenish your supply before it runs low.</p>
                    </section>
                </main>
            </div>
        );
    }

    return (
        <div className="box-complete-page">
            <header className="box-header">
                <div className="box-header-inner">
                    <img src={banner} alt="WagTag" className="box-logo" />
                    <div className="box-header-label">
                        <strong>WagTag Stock</strong>
                        <span>Stock notification</span>
                    </div>
                </div>
            </header>

            <main className="box-main">
                <section className="box-intro">
                    <h1>Thanks</h1>
                    <p>Letting us know helps ensure there are always enough WagTags on hand to replenish your supply.</p>
                </section>

                <form className="box-form" onSubmit={handleSubmit}>
                    <div className="box-form-heading">
                        <h2>Who finished this box?</h2>
                        <p>All fields are required.</p>
                    </div>

                    <label className="box-field" htmlFor="box-code">
                        <span>Box code</span>
                        <input
                            id="box-code"
                            type="text"
                            value={boxCode}
                            placeholder="Missing box code"
                            readOnly
                            aria-readonly="true"
                        />
                    </label>

                    <label className="box-field" htmlFor="box-contact-name">
                        <span><FiUser aria-hidden="true" /> Name</span>
                        <input
                            id="box-contact-name"
                            type="text"
                            autoComplete="name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Your full name"
                            required
                        />
                    </label>

                    <label className="box-field" htmlFor="box-contact-phone">
                        <span><FiPhone aria-hidden="true" /> Phone number</span>
                        <input
                            id="box-contact-phone"
                            type="tel"
                            autoComplete="tel"
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                            placeholder="e.g. 0412 345 678"
                            required
                        />
                    </label>

                    <label className="box-field" htmlFor="box-location">
                        <span><FiMapPin aria-hidden="true" /> Location</span>
                        <input
                            id="box-location"
                            type="text"
                            autoComplete="organization"
                            value={location}
                            onChange={(event) => setLocation(event.target.value)}
                            placeholder="Enter your location"
                            required
                        />
                    </label>

                    <div className="box-turnstile">
                        {turnstileSiteKey ? (
                            <Turnstile
                                ref={turnstileRef}
                                siteKey={turnstileSiteKey}
                                onSuccess={(token) => {
                                    setTurnstileToken(token);
                                    setTurnstileErrorMessage("");
                                }}
                                onExpire={() => setTurnstileToken("")}
                                onError={() => {
                                    setTurnstileToken("");
                                    setTurnstileErrorMessage("The security check could not be completed. Please try again.");
                                }}
                                options={{
                                    action: "report_box_empty",
                                    appearance: "interaction-only",
                                    size: "flexible",
                                    theme: "light",
                                }}
                            />
                        ) : (
                            <p className="box-error">The security check is not configured. Please contact WagTag support.</p>
                        )}
                        {turnstileErrorMessage && <p className="box-error" role="alert">{turnstileErrorMessage}</p>}
                    </div>

                    {!boxCode && (
                        <p className="box-error" role="alert">This box QR code is missing. Please scan the QR code on the box again.</p>
                    )}
                    {errorMessage && <p className="box-error box-submit-error" role="alert">{errorMessage}</p>}

                    <button className="box-submit" type="submit" disabled={!isComplete || submitting}>
                        <FiCheck aria-hidden="true" /> {submitting ? "Submitting..." : "Submit"}
                    </button>
                </form>
            </main>
        </div>
    );
}

export default BoxComplete;